/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { parseCronExpression } from 'cron-schedule';
import { IntervalBasedCronScheduler } from 'cron-schedule/schedulers/interval-based.js';
import isNil from 'lodash/isNil';

/**
 * @import { Logger } from '../logger/logger'
 * @import ReplacerPower from '../replacer/power/replacer-power'
 */

/**
 * Správce automatického restartu zařízení, který poskytuje rozhraní
 * pro ovládání časovače, který po svém uplynutí způsobí restart.
 */
export default class AutorebootManager {
  /**
   * @param {Logger} logger
   * @param {number} minInterval
   * @param {ReplacerPower} replacerPower
   */
  constructor(logger, minInterval, replacerPower) {
    this.logger = logger;
    this.minInterval = minInterval;
    // TODO: Závislost na replaceru je nutná kvůli mutexu; v budoucnu zvážit separaci.
    this.replacerPower = replacerPower;
    /** @type {IntervalBasedCronScheduler | undefined} */
    this.scheduler = undefined;
    /** @type {number | undefined} */
    this.taskId = undefined;
    this.onError = this.onError.bind(this);
  }

  /**
   * @private
   * @param {Error} error
   */
  onError(error) {
    this.logger.error(error);
  }

  /**
   * Spustí automatický restart.
   * @param {number} interval [ms]
   * @returns {undefined}
   * @throws {Error} Pokud je automatický restart již spuštěn.
   */
  start(interval) {
    if (!isNil(this.scheduler) && !isNil(this.taskId)) {
      throw new Error('Autoreboot is already started.');
    }
    this.scheduler = new IntervalBasedCronScheduler(
      Math.max(interval, this.minInterval)
    );
    // interval definuje každých kolik ms se má spustit úloha,
    // ale cron výraz musí být validní, takže použijeme každou sekundu.
    const cron = parseCronExpression('* * * * * *');
    this.taskId = this.scheduler.registerTask(cron, () => {
      this.replacerPower.reboot().catch(this.onError);
    });
  }

  /**
   * Zastaví automatický restart.
   */
  stop() {
    if (!isNil(this.scheduler) && !isNil(this.taskId)) {
      this.scheduler?.unregisterTask(this.taskId);
      this.scheduler?.stop();
      this.scheduler = undefined;
      this.taskId = undefined;
    }
  }
}
